import React, { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Check, ChevronRight, Clock3, Loader2, Sparkles, Wrench, X } from "lucide-react";
import "./ExecutionProgress.css";
import MessageItem from "./MessageItem";
import BrowserApproval from "./BrowserApproval";

export const elapsedLabel = seconds => {
  const value = Math.max(0, Math.floor(seconds || 0));
  if (value < 60) return `${value}秒`;
  if (value >= 3600) return `${Math.floor(value / 3600)}小时${Math.floor(value % 3600 / 60)}分钟${value % 60}秒`;
  const minutes = Math.floor(value / 60);
  return `${minutes}分钟${String(value % 60).padStart(2, "0")}秒`;
};

const titleFor = (step, labels) => (labels[step?.tool] || step?.label || "处理请求").replace(/^正在/, "");

export function groupSteps(steps) {
  return steps.reduce((groups, step) => {
    const key = `${step.kind}:${step.tool || step.label}${step.tool === "browser" ? `:${step.label}` : ""}`;
    const last = groups[groups.length - 1];
    if (last?.key === key && !["user", "commentary"].includes(step.kind)) last.steps.push(step);
    else groups.push({ key, id: step.id, steps: [step] });
    return groups;
  }, []);
}

function StepGroup({ group, labels }) {
  if (group.steps.length === 1) return <Step step={group.steps[0]} labels={labels} />;
  const Kind = group.steps[0].kind === "tool" ? Wrench : Sparkles;
  return <details className="execution-step execution-group">
    <summary><Kind size={14} aria-hidden="true" /><span>{titleFor(group.steps[0], labels)}</span>
      <span className="execution-group-count">{group.steps.length}次</span>
      <ChevronRight size={12} className="execution-step-chevron" aria-hidden="true" /></summary>
    <div className="execution-group-items">{group.steps.map(step => <Step key={step.id} step={step} labels={labels} />)}</div>
  </details>;
}

function Step({ step, labels }) {
  if (step.kind === "user") return <div className="execution-user-message"><MessageItem msg={{ role: "user", content: step.detail }} /></div>;
  if (step.kind === "commentary") return <div className="execution-commentary">{step.detail}</div>;
  const running = step.status === "running";
  const failed = ["failed", "canceled"].includes(step.status);
  const Kind = step.kind === "tool" ? Wrench : Sparkles;
  const Status = running ? Loader2 : failed ? X : Check;
  return <details className={`execution-step ${running ? "is-running" : ""} ${failed ? "is-failed" : ""}`}>
    <summary>
      <Kind size={14} aria-hidden="true" />
      <span className="execution-step-title">{titleFor(step, labels)}</span>
      {Number.isFinite(step.elapsed_ms) && <span className="execution-step-duration">{elapsedLabel(step.elapsed_ms / 1000)}</span>}
      <Status size={13} className={running ? "spin" : ""} aria-label={running ? "执行中" : failed ? "未完成" : "已完成"} />
      <ChevronRight size={12} className="execution-step-chevron" aria-hidden="true" />
    </summary>
    <div className="execution-step-detail">{step.detail || (failed ? "这一步未能完成。" : running ? "正在等待结果。" : "上游未返回额外详情。")}</div>
    {step.artifacts?.map(file => <div className="browser-artifact" key={file.id}>
      {file.content_type === "image/png" && <a href={file.url} target="_blank" rel="noreferrer"><img src={file.url} alt="网页截图" loading="lazy" /></a>}
      <a href={`${file.url}?download=true`}>{file.filename || "下载文件"}</a>
    </div>)}
  </details>;
}

export default function ExecutionProgress({ job, running = false, labels = {}, onUpdate }) {
  const [expanded, setExpanded] = useState(running);
  const [now, setNow] = useState(Date.now());
  const thoughtRef = useRef(null);
  const followRef = useRef(true);
  const reduceMotion = useReducedMotion();
  const activity = job?.activity || job?.usage?.activity || {};
  const legacySteps = [];
  for (const event of job?.usage?.trace || []) {
    if (event?.type === "ToolCall" && event.name) legacySteps.push({ id: `legacy-${legacySteps.length}`, kind: "tool", tool: event.name, status: "running" });
    if (event?.type === "ToolMessage" && event.name) {
      const step = [...legacySteps].reverse().find(item => item.tool === event.name && item.status === "running");
      if (step) step.status = "completed";
    }
  }
  const steps = Array.isArray(activity.steps) ? activity.steps : legacySteps;
  const processSteps = steps.filter(step => step.kind !== "model");
  const diagnostics = steps.filter(step => step.kind === "model" && !(running && step.id === activity.current_id));
  const current = steps.find(step => step.id === activity.current_id && step.status === "running")
    || [...steps].reverse().find(step => step.status === "running");
  const started = Number(job?.started_at || activity.started_at || job?.created_at) * 1000;
  const end = running ? now : Number(job?.completed_at || activity.completed_at || job?.updated_at) * 1000;
  const elapsed = Number.isFinite(started) && started > 0 && Number.isFinite(end) ? (end-started)/1000 : 0;
  const thinking = running && job?.reasoning_active === true && Boolean(job?.reasoning);
  const fallback = { rag_retrieval: "正在检索知识库", coding_agent: "正在分析代码", video_creation: "正在处理视频任务" }[job?.stage] || "正在处理";
  const state = running ? job?.status === "queued" ? "等待处理" : current ? `正在${titleFor(current, labels)}` : fallback
    : job?.status === "failed" ? "处理未完成" : job?.status === "canceled" ? "已停止" : "处理完成";

  useEffect(() => {
    setExpanded(running);
    followRef.current = true;
  }, [job?.job_id, running]);

  useEffect(() => {
    if (!running) return undefined;
    setNow(Date.now());
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [running, job?.job_id]);

  useEffect(() => {
    if (thoughtRef.current && followRef.current) {
      thoughtRef.current.scrollTop = thoughtRef.current.scrollHeight;
      onUpdate?.();
    }
    // The callback only adjusts scroll, and does not determine streamed content.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.reasoning]);

  const transition = { duration: reduceMotion ? 0 : .18, ease: "easeOut" };
  return <section className={`execution-progress ${running ? "is-active" : "is-finished"}`} aria-label="执行进度">
    {running ? <div className="execution-elapsed"><Clock3 size={12} aria-hidden="true" />已处理 {elapsedLabel(elapsed)}</div>
      : <button type="button" className="execution-current execution-total" onClick={() => setExpanded(value => !value)}
          aria-expanded={expanded} aria-controls={`execution-${job?.job_id || "pending"}`}>
          <Clock3 size={12} aria-hidden="true" /><span>用时 {elapsedLabel(elapsed)}</span>
          <ChevronRight size={13} className={expanded ? "execution-chevron is-open" : "execution-chevron"} aria-hidden="true" />
        </button>}
    <AnimatePresence initial={false}>
      {expanded && <motion.div className="execution-step-list" id={`execution-${job?.job_id || "pending"}`}
        key="steps" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
        exit={{ height: 0, opacity: 0 }} transition={transition} onAnimationComplete={onUpdate}>
        {processSteps.length ? groupSteps(processSteps)
          .map(group => <StepGroup key={group.id} group={group} labels={labels} />)
          : !diagnostics.length && <div className="execution-no-steps">{running ? "正在准备上下文" : "没有额外的操作步骤"}</div>}
        {diagnostics.length > 0 && <details className="execution-diagnostics"><summary>处理详情<ChevronRight size={12} /></summary>
          {groupSteps(diagnostics).map(group => <StepGroup key={group.id} group={group} labels={labels} />)}</details>}
      </motion.div>}
    </AnimatePresence>
    {running && steps.filter(step => step.approval?.state === "pending").map(step =>
      <BrowserApproval key={step.approval.pending_id} jobId={job.job_id} approval={step.approval} />)}
    {running && <button type="button" className="execution-current" onClick={() => setExpanded(value => !value)}
      aria-expanded={expanded} aria-controls={`execution-${job?.job_id || "pending"}`}>
      <span className="execution-live-dot" aria-hidden="true" />
      <span className={running ? "execution-current-text" : ""}>{state}</span>
      <ChevronRight size={13} className={expanded ? "execution-chevron is-open" : "execution-chevron"} aria-hidden="true" />
    </button>}
    <AnimatePresence initial={false}>
      {thinking && <motion.div key="thought" className="execution-thought-shell"
        initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
        transition={transition} onAnimationComplete={onUpdate}>
        <div className="execution-thought" ref={thoughtRef} aria-label="模型思考"
          onScroll={event => { const el = event.currentTarget; followRef.current = el.scrollHeight-el.scrollTop-el.clientHeight < 28; }}>
          {job.reasoning}<span className="execution-caret" aria-hidden="true" />
        </div>
      </motion.div>}
    </AnimatePresence>
  </section>;
}
